import {
  Modal,
  NewButton,
  NewButtonVariant,
} from "@visiblethread/react-components";
import clsx from "clsx";
import { UserPermissionRow } from "components/admin";
import Field, { fieldTypes } from "components/Field";
import { CLOUD_ADMIN_LABEL, ON_PREM_ADMIN_LABEL } from "constants/systemAdmin";
import { modalTypes } from "constants/types";
import classes from "layout/systemAdmin/users/modals/NewUserModal.module.scss";
import PropTypes from "prop-types";
import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { hideModal } from "slices/app/actions/modals";
import { selectIsCloudMode, selectIsGovMode } from "slices/app/selectors";
import { editUser } from "slices/userAdmin/actions/userAdmin";

const EditUserAndPermissionsModal = ({
  workspaceId,
  userName,
  currentFirstName,
  currentLastName,
  currentLicensed,
  currentUserAdmin,
  currentReadabilitySettingsAdmin,
  currentAcronymSettingsAdmin,
  currentDiscoverySettingsAdmin,
  currentExcelOutputsAdmin,
  currentDictionariesAdmin,
  currentProposalStagesAdmin,
  currentDocStyleTemplatesAdmin,
  currentEditCurrenciesAdmin,
  currentArchiveStatusesAdmin,
}) => {
  const dispatch = useDispatch();

  const isCloudMode = useSelector(selectIsCloudMode);

  const [firstName, setFirstName] = useState(currentFirstName);
  const [lastName, setLastName] = useState(currentLastName);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [licensed, setLicensed] = useState(currentLicensed);
  const [userAdmin, setUserAdmin] = useState(currentUserAdmin);
  const [readabilitySettingsAdmin, setReadabilitySettingsAdmin] = useState(
    currentReadabilitySettingsAdmin
  );
  const [acronymSettingsAdmin, setAcronymSettingsAdmin] = useState(
    currentAcronymSettingsAdmin
  );
  const [discoverySettingsAdmin, setDiscoverySettingsAdmin] = useState(
    currentDiscoverySettingsAdmin
  );
  const [excelOutputsAdmin, setExcelOutputsAdmin] = useState(
    currentExcelOutputsAdmin
  );
  const [dictionariesAdmin, setDictionariesAdmin] = useState(
    currentDictionariesAdmin
  );
  const [proposalStagesAdmin, setProposalStagesAdmin] = useState(
    currentProposalStagesAdmin
  );
  const [docStyleTemplatesAdmin, setDocStyleTemplatesAdmin] = useState(
    currentDocStyleTemplatesAdmin
  );
  const [editCurrenciesAdmin, setEditCurrenciesAdmin] = useState(
    currentEditCurrenciesAdmin
  );
  const [archiveStatusesAdmin, setArchiveStatusesAdmin] = useState(
    currentArchiveStatusesAdmin
  );

  const [firstNameError, setFirstNameError] = useState(false);
  const [lastNameError, setLastNameError] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  const [confirmPasswordError, setConfirmPasswordError] = useState("");

  const isUserNamePassword = useSelector(
    (state) => state?.systemAdmin.isUsernamePassword
  );
  const userNamePassEnabled = () =>
    firstNameError === false &&
    lastNameError === false &&
    passwordError === "" &&
    confirmPasswordError === "";
  const ssoDisabled = () => firstNameError === false && lastNameError === false;
  const isOkButtonEnabled = () =>
    isUserNamePassword ? userNamePassEnabled() : ssoDisabled();
  const isGovMode = useSelector(selectIsGovMode);

  const handleEditUser = () => {
    if (isOkButtonEnabled()) {
      dispatch(
        editUser(
          workspaceId,
          userName,
          firstName,
          lastName,
          password,
          licensed,
          userAdmin,
          readabilitySettingsAdmin,
          acronymSettingsAdmin,
          discoverySettingsAdmin,
          excelOutputsAdmin,
          dictionariesAdmin,
          proposalStagesAdmin,
          docStyleTemplatesAdmin,
          editCurrenciesAdmin,
          archiveStatusesAdmin
        )
      );
    }
  };

  const onEnterKeyDown = ({ key }) => {
    if (key === "Enter" && isOkButtonEnabled()) {
      handleEditUser();
    }
  };

  const handleCancel = () => {
    dispatch(hideModal(modalTypes.EDIT_USER_AND_PERMISSIONS));
  };

  const userNameField = () => (
    <label
      htmlFor="edit-user-modal-user-name"
      className="edit__user__modal__username"
    >
      <b>User Name</b>
      <input
        type="text"
        id="edit-user-modal-user-name"
        value={userName}
        disabled
        data-clarity-mask="True"
      />
    </label>
  );

  const handleLicensedChange = () => {
    if (licensed) {
      setDictionariesAdmin(false);
      setReadabilitySettingsAdmin(false);
      setAcronymSettingsAdmin(false);
      setDiscoverySettingsAdmin(false);
      setExcelOutputsAdmin(false);
      setProposalStagesAdmin(false);
      setDocStyleTemplatesAdmin(false);
      setEditCurrenciesAdmin(false);
      setArchiveStatusesAdmin(false);
    }
    setLicensed(!licensed);
  };

  return (
    <Modal
      data-test="editUserModal"
      className={clsx("fade-in", classes.modal_container)}
      show
      onDismiss={handleCancel}
      backdrop
      centered
      onKeyDown={onEnterKeyDown}
    >
      <Modal.Header>
        <b className="edit__user__modal__header">Edit User</b>
      </Modal.Header>
      <Modal.Body className={classes.modal_body}>
        <div className="edit__user__modal">
          {userNameField()}
          <Field
            type={fieldTypes.firstName}
            propsObject={{
              firstName,
              setFirstName,
              firstNameError,
              setFirstNameError,
            }}
          />

          <Field
            type={fieldTypes.lastName}
            propsObject={{
              lastName,
              setLastName,
              lastNameError,
              setLastNameError,
            }}
          />

          {!isGovMode && isUserNamePassword && (
            <Field
              type={fieldTypes.passwordAndConfirm}
              propsObject={{
                password,
                setPassword,
                passwordError,
                setPasswordError,
                confirmPassword,
                setConfirmPassword,
                confirmPasswordError,
                setConfirmPasswordError,
              }}
            />
          )}
        </div>
        <div className="edit__permissions__modal">
          <UserPermissionRow
            heading
            checked={licensed}
            disabled={false}
            id="edit-permissions-licensed"
            onChange={handleLicensedChange}
            text="Is Licensed User"
            tooltipText="Making a user 'active' allows them sign in and work with documents. Only 'Active' users use a license."
          />
          <UserPermissionRow
            checked={dictionariesAdmin}
            disabled={!licensed}
            id="edit-permissions-dictionaries"
            onChange={() => setDictionariesAdmin(!dictionariesAdmin)}
            text="Can Edit Dictionaries"
            tooltipText="Enabling this permission allows this user create and modify dictionaries."
          />
          <UserPermissionRow
            checked={readabilitySettingsAdmin}
            disabled={!licensed}
            id="edit-permissions-readability-settings"
            onChange={() =>
              setReadabilitySettingsAdmin(!readabilitySettingsAdmin)
            }
            text="Can Edit Readability Settings"
            tooltipText="Enabling this permission allows this user edit the Readability Settings for this workspace."
          />
          <UserPermissionRow
            checked={acronymSettingsAdmin}
            disabled={!licensed}
            id="edit-permissions-acronym-settings"
            onChange={() => setAcronymSettingsAdmin(!acronymSettingsAdmin)}
            text="Can Edit Acronym Settings"
            tooltipText="Enabling this permission allows this user edit the Acronym Settings for this workspace."
          />
          <UserPermissionRow
            checked={discoverySettingsAdmin}
            disabled={!licensed}
            id="edit-permissions-discovery-settings"
            onChange={() => setDiscoverySettingsAdmin(!discoverySettingsAdmin)}
            text="Can Edit Discovery Settings"
            tooltipText="Enabling this permission allows this user edit the Discovery Settings for this workspace."
          />
          <UserPermissionRow
            checked={excelOutputsAdmin}
            disabled={!licensed}
            id="edit-permissions-excel__outputs"
            onChange={() => setExcelOutputsAdmin(!excelOutputsAdmin)}
            text="Can Edit Excel Outputs"
            tooltipText="Enabling this permission allows this user edit the Excel Outputs for this workspace."
          />
          <UserPermissionRow
            checked={proposalStagesAdmin}
            disabled={!licensed}
            id="edit-permissions-proposal-stages"
            onChange={() => setProposalStagesAdmin(!proposalStagesAdmin)}
            text="Can Edit Pipeline Stages"
            tooltipText="Enabling this permission allows this user edit the Pipeline Stages for this workspace."
          />
          <UserPermissionRow
            checked={docStyleTemplatesAdmin}
            disabled={!licensed}
            id="edit-permissions-templates"
            onChange={() => setDocStyleTemplatesAdmin(!docStyleTemplatesAdmin)}
            text="Can Edit Style Templates"
            tooltipText="Enabling this permission allows this user edit the Templates for this workspace."
          />
          <UserPermissionRow
            checked={editCurrenciesAdmin}
            disabled={!licensed}
            id="edit-permissions-currencies"
            onChange={() => setEditCurrenciesAdmin(!editCurrenciesAdmin)}
            text="Can Edit Currencies"
            tooltipText="Enabling this permission allows this user edit the Currencies for this workspace."
          />
          <UserPermissionRow
            checked={archiveStatusesAdmin}
            disabled={!licensed}
            id="edit-permissions-archive-statuses"
            onChange={() => setArchiveStatusesAdmin(!archiveStatusesAdmin)}
            text="Edit Archive Statuses"
            tooltipText="Enabling this permission allows this user edit the Archive Statuses for this workspace."
          />
          <UserPermissionRow
            heading
            checked={userAdmin}
            disabled={false}
            id="edit-permissions-user-admin"
            onChange={() => setUserAdmin(!userAdmin)}
            text={isCloudMode ? CLOUD_ADMIN_LABEL : ON_PREM_ADMIN_LABEL}
            tooltipText="This allows the user create modify and delete users within the workspace."
          />
        </div>
      </Modal.Body>
      <Modal.Footer>
        <NewButton
          data-test="cancelButton"
          variant={NewButtonVariant.l2}
          onClick={handleCancel}
        >
          Cancel
        </NewButton>

        <NewButton
          data-test="saveButton"
          variant={NewButtonVariant.l1}
          onClick={handleEditUser}
          disabled={!isOkButtonEnabled()}
        >
          Save
        </NewButton>
      </Modal.Footer>
    </Modal>
  );
};

export default EditUserAndPermissionsModal;

EditUserAndPermissionsModal.propTypes = {
  workspaceId: PropTypes.number.isRequired,
  userName: PropTypes.string.isRequired,
  currentFirstName: PropTypes.string.isRequired,
  currentLastName: PropTypes.string.isRequired,
  currentLicensed: PropTypes.bool.isRequired,
  currentUserAdmin: PropTypes.bool.isRequired,
  currentReadabilitySettingsAdmin: PropTypes.bool.isRequired,
  currentAcronymSettingsAdmin: PropTypes.bool.isRequired,
  currentDiscoverySettingsAdmin: PropTypes.bool.isRequired,
  currentProposalStagesAdmin: PropTypes.bool.isRequired,
  currentDocStyleTemplatesAdmin: PropTypes.bool.isRequired,
  currentEditCurrenciesAdmin: PropTypes.bool.isRequired,
  currentArchiveStatusesAdmin: PropTypes.bool.isRequired,
  currentExcelOutputsAdmin: PropTypes.bool.isRequired,
  currentDictionariesAdmin: PropTypes.bool.isRequired,
};
